import { ApiResourceBaseClass as n } from "./baseApiCalls.js";
const s = "conversation_starters";
class c extends n {
  /**
   * Retrieves all conversation starters.
   *
   * HTTP Verb: GET
   * Endpoint: /conversation_starters
   *
   * @returns {Promise<ConversationStarter[]>} A promise that resolves to an array of conversation starters.
   */
  getConversationStarters = async () => {
    const { data: t } = await this.get(s);
    return t;
  };
  /**
   * Retrieves a single conversation starter by ID.
   *
   * HTTP Verb: GET
   * Endpoint: /conversation_starters/{conversationStarterId}
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @returns {Promise<ConversationStarter>} A promise that resolves to the conversation starter.
   */
  getConversationStarter = async (t) => {
    const { data: a } = await this.get(
      `${s}/${t}`
    );
    return a;
  };
  /**
   * Retrieves items for a specific conversation starter.
   *
   * HTTP Verb: GET
   * Endpoint: /conversation_starters/{conversationStarterId}/conversation_starter_items
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @returns {Promise<ConversationStarterItemsGetResponse>} A promise that resolves to the conversation starter items.
   */
  getConversationStartersItems = async (t) => {
    const { data: a } = await this.get(
      `${s}/${t}/conversation_starter_items`
    );
    return a;
  };
  /**
   * Creates a new conversation starter.
   *
   * HTTP Verb: POST
   * Endpoint: /conversation_starters
   *
   * @returns {Promise<ConversationStarter>} A promise that resolves to the newly created conversation starter.
   */
  createConversationStarter = async () => {
    const { data: t } = await this.post(s);
    return t;
  };
  /**
   * Duplicates an existing conversation starter.
   *
   * HTTP Verb: POST
   * Endpoint: /conversation_starters
   *
   * @param conversationStarterId - The ID of the conversation starter to duplicate.
   * @returns {Promise<ConversationStarter>} A promise that resolves to the duplicated conversation starter.
   */
  duplicateConversationStarter = async (t) => {
    const { data: a } = await this.post(s, {
      duplicate_conversation_starter_id: t
    });
    return a;
  };
  /**
   * Deletes a conversation starter.
   *
   * HTTP Verb: DELETE
   * Endpoint: /conversation_starters/{conversationStarterId}
   *
   * @param conversationStarterId - The ID of the conversation starter to delete.
   * @returns {Promise<ConversationStarter>} A promise that resolves to the deleted conversation starter.
   */
  deleteConversationStarter = async (t) => {
    const { data: a } = await this.delete(
      `${s}/${t}`
    );
    return a;
  };
  /**
   * Updates a conversation starter.
   *
   * HTTP Verb: PATCH
   * Endpoint: /conversation_starters/{conversationStarterId}
   *
   * @param conversationStarterId - The ID of the conversation starter to update.
   * @param payload - The payload with updated conversation starter data.
   * @returns {Promise<ConversationStarter>} A promise that resolves to the updated conversation starter.
   */
  updateConversationStarter = async (t, a) => {
    const { data: e } = await this.patch(
      `${s}/${t}`,
      a
    );
    return e;
  };
  /**
   * Updates the businesses associated with a conversation starter.
   *
   * HTTP Verb: PATCH
   * Endpoint: /conversation_starters/{conversationStarterId}/business-selection
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param businessSearchFilter - The filter criteria for business selection.
   * @returns {Promise<ConversationStarter>} A promise that resolves to the updated conversation starter.
   */
  updateConversationStarterBusinesses = async (t, a) => {
    const e = { business_search_params: a }, { data: r } = await this.patch(
      `${s}/${t}/business-selection`,
      e
    );
    return r;
  };
  /**
   * Activates or deactivates a conversation starter.
   *
   * HTTP Verb: PATCH
   * Endpoint: /conversation_starters/{conversationStarterId}/activation
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param enable - Boolean flag to enable (true) or disable (false) the conversation starter.
   * @returns {Promise<ConversationStarter>} A promise that resolves to the updated conversation starter.
   */
  activateConversationStarter = async (t, a) => {
    const e = { enable: a }, { data: r } = await this.patch(
      `${s}/${t}/activation`,
      e
    );
    return r;
  };
  /**
   * Creates a new conversation starter item.
   *
   * HTTP Verb: POST
   * Endpoint: /conversation_starters/{conversationStarterId}/conversation_starter_items
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param payload - The payload for the new conversation starter item.
   * @returns {Promise<ConversationStarterItem>} A promise that resolves to the newly created conversation starter item.
   */
  createConversationStarterItem = async (t, a) => {
    const { data: e } = await this.post(
      `${s}/${t}/conversation_starter_items`,
      a
    );
    return e;
  };
  /**
   * Updates an existing conversation starter item.
   *
   * HTTP Verb: PATCH
   * Endpoint: /conversation_starters/{conversationStarterId}/conversation_starter_items/{conversationStarterItemId}
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param conversationStarterItemId - The ID of the conversation starter item.
   * @param payload - The payload with updated item data.
   * @returns {Promise<ConversationStarterItem>} A promise that resolves to the updated conversation starter item.
   */
  updateConversationStarterItem = async (t, a, e) => {
    const { data: r } = await this.patch(
      `${s}/${t}/conversation_starter_items/${a}`,
      e
    );
    return r;
  };
  /**
   * Updates the order of conversation starter items.
   *
   * HTTP Verb: PATCH
   * Endpoint: /conversation_starters/{conversationStarterId}/conversation_starter_items
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param reorderedConversationStarterIds - Array of conversation starter item IDs in the new order.
   * @returns {Promise<string>} A promise that resolves to a response message or updated order.
   */
  updateConversationStarterItemOrder = async (t, a) => {
    const { data: e } = await this.patch(
      `${s}/${t}/conversation_starter_items`,
      { cs_items_ids: a }
    );
    return e;
  };
  /**
   * Deletes a conversation starter item.
   *
   * HTTP Verb: DELETE
   * Endpoint: /conversation_starters/{conversationStarterId}/conversation_starter_items/{conversationStarterItemId}
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param conversationStarterItemId - The ID of the conversation starter item to delete.
   * @returns {Promise<any>} A promise that resolves when the item is deleted.
   */
  deleteConversationStarterItem = async (t, a) => this.delete(
    `${s}/${t}/conversation_starter_items/${a}`
  );
  /**
   * Validates the business selection for a conversation starter.
   *
   * HTTP Verb: POST
   * Endpoint: /conversation_starters/{conversationStarterId}/validate-business-selection
   *
   * @param conversationStarterId - The ID of the conversation starter.
   * @param businessSearchFilter - The filter criteria for validating businesses.
   * @returns {Promise<BusinessValidationResponse>} A promise that resolves to the validation response.
   */
  validateBusinesses = async (t, a) => {
    const e = { business_search_params: a }, { data: r } = await this.post(
      `${s}/${t}/validate-business-selection`,
      e
    );
    return r;
  };
}
export {
  c as ConversationStarters
};
